import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { FormLabelItem, TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { ActivityType } from '@/shared/lib/sj-orm/models/asset.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { LegalEntityDetails, LegalEntityGeneral } from '../../sj-orm/models/assets/categories';
import {
  FormOfOrganizationType,
  LegalEntityUsageProfileType,
  TaxAgentType,
} from '../../sj-orm/models/assets/enums';
import { ContactType } from '../../sj-orm/models/contact.dto';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type LegalEntity = LegalEntityFormGeneralType &
  LegalEntityFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

const FormOfOrganizationList = [
  {
    label: 'entities/assets:formOfOrganizationEnum.public',
    key: FormOfOrganizationType.public,
  },
  {
    label: 'entities/assets:formOfOrganizationEnum.private',
    key: FormOfOrganizationType.individual,
  },
];

const LegalEntityUsageProfileList = [
  {
    key: LegalEntityUsageProfileType.personal,
    label: 'entities/assets:usageProfileEnum.personal',
  },
  {
    key: LegalEntityUsageProfileType.commercial,
    label: 'entities/assets:usageProfileEnum.commercial',
  },
  {
    key: LegalEntityUsageProfileType.investment,
    label: 'entities/assets:usageProfileEnum.investment',
  },
  {
    key: LegalEntityUsageProfileType.strategic,
    label: 'entities/assets:usageProfileEnum.strategic',
  },
  {
    key: LegalEntityUsageProfileType.other,
    label: 'entities/assets:usageProfileEnum.other',
  },
];

const TaxAgentList = [
  {
    key: TaxAgentType.company,
    label: 'entities/assets:taxAgentEnum.company',
  },
  {
    key: TaxAgentType.person,
    label: 'entities/assets:taxAgentEnum.person',
  },
  {
    key: TaxAgentType.other,
    label: 'entities/assets:taxAgentEnum.other',
  },
];

type LegalEntityHelpGeneral = {
  nickname: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  entityType: QuestionType.SELECT_FROM_LIST;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
  legalName: QuestionType.TEXT;
};

type LegalEntityHelpDetails = {
  formOfOrganization?: QuestionType.SELECT_FROM_LIST;
  structure?: QuestionType.TEXT;
  typeOfBusiness?: QuestionType.TEXT;
  myAssets: QuestionType.PICK_ARRAY_OF_DTO;
  dividendTaxAgent?: QuestionType.SELECT_FROM_LIST;
  otherDividendTaxAgent?: QuestionType.TEXT;
  longTermShareholderLiabilities?: QuestionType.NUMBER;
  longTermCompanyLiabilities?: QuestionType.NUMBER;
  isCompanyHasLimitations?: QuestionType.CHECKBOX;
  companyLimitationsComment?: QuestionType.TEXT_MULTILINE;
  isShareholderHasLimitations?: QuestionType.CHECKBOX;
  shareholderLimitationsComment?: QuestionType.TEXT_MULTILINE;
  usageProfile?: QuestionType.SELECT_FROM_LIST;
  dateOfIncorporation?: QuestionType.DATE_TIME;
  taxNumber?: QuestionType.TEXT;
  taxNumberIssuedBy?: QuestionType.TEXT;
  lei?: QuestionType.TEXT;
  address?: QuestionType.ADDRESS;
  province?: QuestionType.TEXT;
  zipCode?: QuestionType.TEXT;
  city?: QuestionType.TEXT;
  street?: QuestionType.TEXT;
  buildingNumber?: QuestionType.TEXT;
  apartment?: QuestionType.TEXT;
  addressComment?: QuestionType.TEXT_MULTILINE;
  contacts?: QuestionType.PICK_ARRAY_OF_DTO;
  annualReports?: QuestionType.PICK_ARRAY_OF_DTO;
  assetInfoDocuments?: QuestionType.PICK_ARRAY_OF_DTO;
  powers?: QuestionType.PICK_ARRAY_OF_DTO;
  attachments?: QuestionType.FILE_UPLOAD;
};

type LegalEntityFormGeneralType = TDtoWrapper<LegalEntityGeneral, LegalEntityHelpGeneral>;
type LegalEntityFormDetailsType = TDtoWrapper<LegalEntityDetails, LegalEntityHelpDetails>;

const LegalEntityFormGeneral: LegalEntityFormGeneralType = {
  nickname: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  // entityType: {
  //   type: QuestionType.SELECT_FROM_LIST,
  //   label: 'forms_assets:legalEntity.inputs.type.label',
  //   value: ActivityType.active,
  //   required: true,
  //   items: ActivityList,
  // },
  passiveType: {
    type: FormLabelItem.text,
    label: 'forms_assets:legalEntity.inputs.passiveType.label',
    showIf: {
      entityType: {
        value: ActivityType.passive,
      },
    },
  },
  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:legalEntity.inputs.capitalization.label',
    tooltip: 'forms_assets:legalEntity.inputs.capitalization.tooltip',
    equivalentValueTooltip:
      // eslint-disable-next-line sonarjs/no-duplicate-string
      'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  legalName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:legalEntity.inputs.legalName.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:legalEntity.inputs.legalName.errors.required'),
  },
};

const LegalEntityFormDetails: LegalEntityFormDetailsType = {
  myAssets: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:legalEntity.inputs.myAssets.label',
    tooltip: 'forms_assets:legalEntity.inputs.myAssets.tooltip',
    items: CollectionName.ASSETS,
    mode: 'multiple',
    order: 3,
  },
  formOfOrganization: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:legalEntity.inputs.formOfOrganization.label',
    items: FormOfOrganizationList,
    order: 4,
  },
  structure: {
    type: QuestionType.TEXT,
    label: 'forms_assets:legalEntity.inputs.structure.label',
    order: 5,
  },
  typeOfBusiness: {
    type: QuestionType.TEXT,
    label: 'forms_assets:legalEntity.inputs.typeOfBusiness.label',
    order: 6,
  },
  dividendTaxAgent: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:legalEntity.inputs.dividendTaxAgent.label',
    items: TaxAgentList,
    order: 7,
  },
  otherDividendTaxAgent: {
    type: QuestionType.TEXT,
    label: 'forms_assets:legalEntity.inputs.otherDividendTaxAgent.label',
    showIf: {
      dividendTaxAgent: {
        value: TaxAgentType.other,
      },
    },
    order: 8,
  },
  longTermShareholderLiabilities: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:legalEntity.inputs.longTermShareholderLiabilities.label',
    order: 9,
  },
  longTermCompanyLiabilities: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:legalEntity.inputs.longTermCompanyLiabilities.label',
    order: 10,
  },
  isCompanyHasLimitations: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:legalEntity.inputs.isCompanyHasLimitations.label',
    order: 11,
  },
  companyLimitationsComment: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:legalEntity.inputs.companyLimitationsComment.label',
    showIf: {
      isCompanyHasLimitations: {
        value: true,
      },
    },
    order: 12,
  },
  isShareholderHasLimitations: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:legalEntity.inputs.isShareholderHasLimitations.label',
    order: 13,
  },
  shareholderLimitationsComment: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:legalEntity.inputs.shareholderLimitationsComment.label',
    showIf: {
      isShareholderHasLimitations: {
        value: true,
      },
    },
    order: 14,
  },
  usageProfile: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:legalEntity.inputs.usageProfile.label',
    items: LegalEntityUsageProfileList,
    order: 15,
  },
  dateOfIncorporation: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:legalEntity.inputs.dateOfIncorporation.label',
    tooltip: 'forms_assets:legalEntity.inputs.dateOfIncorporation.tooltip',
    order: 16,
  },
  taxNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:legalEntity.inputs.taxNumber.label',
    order: 17,
  },
  taxNumberIssuedBy: {
    type: QuestionType.TEXT,
    label: 'forms_assets:legalEntity.inputs.taxNumberIssuedBy.label',
    order: 18,
  },
  lei: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.lei.label',
    tooltip: 'forms_assets:common.inputs.lei.tooltip',
    order: 19,
  },
  address: {
    type: QuestionType.ADDRESS,
    label: 'forms_assets:common.inputs.address.label',
    tooltip: 'forms_assets:common.inputs.address.tooltip',
    order: 20,
  },
  legalEntityAddress: {
    type: FormLabelItem.label,
    label: 'forms_assets:common.inputs.legalEntityAddress.label',
    order: 20,
  },
  addressComment: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:legalEntity.inputs.comments.label',
    order: 27,
  },
  contacts: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:legalEntity.inputs.contacts.label',
    items: CollectionName.CONTACTS,
    mode: 'multiple',
    dtoType: [
      ContactType.INDIVIDUAL,
      ContactType.LEGAL_ENTITY,
      ContactType.BROKER,
      ContactType.BANK,
    ],
    order: 28,
  },
  attachments: baseAssetFields.attachments,
};

const LegalEntityForm = [
  LegalEntityFormGeneral,
  LegalEntityFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder(['plannedDividends', 'actualDividends', 'dividendsDelta']),
  assetsStatisticsBuilder([
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
    'limitations',
    'limitationsComments',
    'specificsOfTaxation',
    'taxAgent',
    'taxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { LegalEntityForm };
